import { useState, useMemo } from 'react';
import styles from './EstoqueModal.module.css';

// ====================================================================
// HELPERS
// ====================================================================

function corDoAvatar(nome) {
  const cores = [
    { bg: '#f0e6fb', text: '#5B2A8C' },
    { bg: '#FCEBEB', text: '#A32D2D' },
    { bg: '#E1F5EE', text: '#0F6E56' },
    { bg: '#EEEDFE', text: '#534AB7' },
    { bg: '#FFF8E6', text: '#7A5200' },
    { bg: '#E6F1FB', text: '#185FA5' },
  ];
  const hash = (nome.charCodeAt(0) + (nome.charCodeAt(1) || 0)) % cores.length;
  return cores[hash];
}

function primeiroNome(nome) {
  return nome.split(' ')[0];
}

const PERFIL_BADGE = {
  Gestor: 'rpbGestor',
  Operador: 'rpbOperador',
};

// ====================================================================
// COMPONENTE
// ====================================================================

export default function EstoqueModal({
  estoque,
  usuarios = [],
  produtos = [],
  onSalvar,
  onCancelar,
  salvando = false,
}) {
  const editando = !!estoque;

  const [nome, setNome] = useState(estoque?.nome || '');
  const [descricao, setDescricao] = useState(estoque?.descricao || '');
  const [responsaveis, setResponsaveis] = useState(estoque?.responsaveis || []);
  const [niveis, setNiveis] = useState(estoque?.niveis || []);
  const [ativo, setAtivo] = useState(estoque?.ativo ?? true);

  const [pickerAberto, setPickerAberto] = useState(false);
  const [buscaProduto, setBuscaProduto] = useState('');

  const valido = nome.trim().length > 0;

  // ===== Responsáveis =====

  function toggleResponsavel(userId) {
    if (responsaveis.includes(userId)) {
      setResponsaveis(responsaveis.filter((id) => id !== userId));
    } else {
      setResponsaveis([...responsaveis, userId]);
    }
  }

  function removerResponsavel(userId) {
    setResponsaveis(responsaveis.filter((id) => id !== userId));
  }

  // ===== Níveis =====

  const produtosFiltradosPicker = useMemo(() => {
    const ativos = produtos.filter((p) => p.ativo !== false);
    if (!buscaProduto) return ativos;
    const q = buscaProduto.toLowerCase();
    return ativos.filter(
      (p) =>
        (p.cor || '').toLowerCase().includes(q) ||
        (p.resina || '').toLowerCase().includes(q)
    );
  }, [buscaProduto, produtos]);

  function dadosProduto(produtoId) {
    return produtos.find((p) => p.id === produtoId);
  }

  function abrirPicker() {
    setBuscaProduto('');
    setPickerAberto(true);
  }

  function adicionarNivel(produto) {
    const jaTem = niveis.some((n) => n.produtoId === produto.id);
    if (jaTem) return;
    setNiveis([...niveis, { produtoId: produto.id, minimo: '', maximo: '' }]);
    setPickerAberto(false);
  }

  function atualizarNivel(produtoId, campo, valor) {
    setNiveis(
      niveis.map((n) =>
        n.produtoId === produtoId ? { ...n, [campo]: valor } : n
      )
    );
  }

  function removerNivel(produtoId) {
    setNiveis(niveis.filter((n) => n.produtoId !== produtoId));
  }

  // ===== Salvar =====

  function handleSalvar() {
    if (!valido) {
      alert('Preencha o nome do estoque.');
      return;
    }
    const niveisNormalizados = niveis.map((n) => ({
      produtoId: n.produtoId,
      minimo: n.minimo === '' ? null : parseInt(n.minimo, 10),
      maximo: n.maximo === '' ? null : parseInt(n.maximo, 10),
    }));
    onSalvar({
      nome: nome.trim(),
      descricao: descricao.trim(),
      responsaveis,
      niveis: niveisNormalizados,
      ativo,
    });
  }

  return (
    <div className={`${styles.modalOverlay} ${styles.aberto}`}>
      <div className={styles.modal}>
        <div className={styles.modalHead}>
          <div>
            <div className={styles.modalTitle}>
              {editando ? `Editar estoque — ${estoque.nome}` : 'Novo estoque'}
            </div>
            <div className={styles.modalSubtitle}>
              Configure o estoque, responsáveis e níveis mín/máx.
            </div>
          </div>
          <button className={styles.modalClose} onClick={onCancelar}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Seção 1: Identificação */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <span>Identificação</span>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>
                Nome do estoque <span className={styles.obrigatorio}>*</span>
              </label>
              <input
                className={styles.formInput}
                placeholder="Ex: DEF, ALM-2, CAMPO"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
              <div className={styles.formHint}>
                Nome curto usado nas movimentações e tabelas.
              </div>
            </div>
            <div className={styles.formField} style={{ marginTop: 12 }}>
              <label className={styles.formLabel}>Descrição</label>
              <textarea
                className={styles.formTextarea}
                placeholder="Ex: Depósito central. Recebimento de compras..."
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
          </div>

          {/* Seção 2: Responsáveis */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <span>Responsáveis pelo estoque</span>
            </div>
            <div className={styles.respSelector}>
              {responsaveis.length > 0 && (
                <div className={styles.respSelectedList}>
                  {responsaveis.map((userId) => {
                    const u = usuarios.find((x) => x.id === userId);
                    if (!u) return null;
                    const cor = corDoAvatar(u.nome || u.email || userId);
                    return (
                      <span key={userId} className={styles.respTagSelected}>
                        <span
                          className={styles.respAvatar}
                          style={{ background: cor.bg, color: cor.text }}
                        >
                          {(u.nome || u.email || userId).charAt(0)}
                        </span>
                        {primeiroNome(u.nome || u.email || userId)}
                        <span
                          className={styles.respTagX}
                          onClick={() => removerResponsavel(userId)}
                        >
                          ✕
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
              {usuarios.length === 0 ? (
                <div className={styles.infoBox}>
                  ⚠ Nenhum usuário disponível. Cadastre Gestores ou Operadores em
                  Configurações para poder atribuir responsáveis.
                </div>
              ) : (
                <div className={styles.respOptions}>
                  {usuarios.map((u) => {
                    const jaSelecionado = responsaveis.includes(u.id);
                    const cor = corDoAvatar(u.nome || u.email || u.id);
                    return (
                      <div
                        key={u.id}
                        className={`${styles.respOption} ${jaSelecionado ? styles.jaSelecionado : ''}`}
                        onClick={() => !jaSelecionado && toggleResponsavel(u.id)}
                      >
                        <span
                          className={styles.respAvatarBig}
                          style={{ background: cor.bg, color: cor.text }}
                        >
                          {(u.nome || u.email || u.id).charAt(0)}
                        </span>
                        <div className={styles.respOptionInfo}>
                          <div className={styles.respOptionNome}>{u.nome || u.email || u.id}</div>
                          <div className={styles.respOptionPerfil}>{u.email || ''}</div>
                        </div>
                        <span
                          className={`${styles.respPerfilBadge} ${styles[PERFIL_BADGE[u.perfil] || '']}`}
                        >
                          {u.perfil}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {usuarios.length > 0 && (
                <div className={styles.infoBox}>
                  ⚠ Apenas <strong>Gestor</strong> ou <strong>Operador</strong> podem ser
                  responsáveis. Visualizadores não aparecem.
                </div>
              )}
            </div>
          </div>

          {/* Seção 3: Níveis (sob demanda) */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <span>Níveis de estoque (mín / máx)</span>
              <button className={styles.niveisAddBtn} onClick={abrirPicker}>
                + Adicionar produto
              </button>
            </div>

            {niveis.length === 0 ? (
              <div className={styles.niveisEmpty}>
                <div className={styles.niveisEmptyIcon}>📊</div>
                Nenhum produto com nível configurado.
                <br />
                Clique em <strong>+ Adicionar produto</strong> para definir mín/máx.
              </div>
            ) : (
              <>
                <div className={styles.niveisTable}>
                  <div className={styles.niveisTableHead}>
                    <div>Produto</div>
                    <div>Mínimo</div>
                    <div>Máximo</div>
                    <div></div>
                  </div>
                  {niveis.map((nivel) => {
                    const p = dadosProduto(nivel.produtoId);
                    return (
                      <div key={nivel.produtoId} className={styles.niveisRow}>
                        <div>
                          {p ? (
                            <>
                              <div className={styles.niveisProdNome}>{p.cor || '—'}</div>
                              <div className={styles.niveisProdCor}>
                                {p.cor ? `${p.cor} · ` : ''}{p.resina || ''}
                              </div>
                            </>
                          ) : (
                            <div className={styles.niveisProdNome} style={{ color: '#aaa' }}>
                              produto removido
                            </div>
                          )}
                        </div>
                        <input
                          className={styles.niveisInput}
                          type="number"
                          min="0"
                          placeholder="—"
                          value={nivel.minimo}
                          onChange={(e) =>
                            atualizarNivel(nivel.produtoId, 'minimo', e.target.value)
                          }
                        />
                        <input
                          className={styles.niveisInput}
                          type="number"
                          min="0"
                          placeholder="—"
                          value={nivel.maximo}
                          onChange={(e) =>
                            atualizarNivel(nivel.produtoId, 'maximo', e.target.value)
                          }
                        />
                        <button
                          className={styles.btnRemoverNivel}
                          title="Remover"
                          onClick={() => removerNivel(nivel.produtoId)}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.niveisResumo}>
                  💡 Adicione apenas os produtos que precisam de alerta neste estoque. O
                  resto não dispara aviso.
                </div>
              </>
            )}
          </div>

          {/* Seção 4: Status */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <span>Status</span>
            </div>
            <div className={styles.toggleStatus}>
              <div
                className={`${styles.toggleSwitch} ${ativo ? styles.ativo : ''}`}
                onClick={() => setAtivo(!ativo)}
              />
              <div className={styles.toggleLabel}>
                Estoque {ativo ? 'ativo' : 'inativo'}
                <small>Estoques inativos não aparecem nas movimentações.</small>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFoot}>
          <button className={styles.btnSecondary} onClick={onCancelar}>
            Cancelar
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSalvar}
            disabled={!valido || salvando}
          >
            {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar estoque'}
          </button>
        </div>
      </div>

      {/* PICKER DE PRODUTO */}
      {pickerAberto && (
        <div className={`${styles.pickerOverlay} ${styles.aberto}`}>
          <div className={styles.pickerModal}>
            <div className={styles.pickerTitle}>Adicionar produto aos níveis</div>
            <div className={styles.pickerSub}>
              Selecione o produto que terá controle de mín/máx neste estoque.
            </div>
            <input
              className={styles.pickerSearch}
              placeholder="🔍 Buscar produto, cor, resina..."
              value={buscaProduto}
              onChange={(e) => setBuscaProduto(e.target.value)}
              autoFocus
            />
            <div className={styles.pickerList}>
              {produtosFiltradosPicker.length === 0 ? (
                <div className={styles.pickerEmpty}>Nenhum produto encontrado.</div>
              ) : (
                produtosFiltradosPicker.map((p) => {
                  const ja = niveis.some((n) => n.produtoId === p.id);
                  return (
                    <div
                      key={p.id}
                      className={`${styles.pickerItem} ${ja ? styles.ja : ''}`}
                      onClick={() => !ja && adicionarNivel(p)}
                    >
                      <div>
                        <div className={styles.pickerNome}>{p.cor || '—'}</div>
                        <div className={styles.pickerMeta}>
                          {p.cor ? `${p.cor} · ` : ''}{p.resina || ''}
                        </div>
                      </div>
                      {ja && (
                        <span style={{ fontSize: 11, color: '#888' }}>
                          já adicionado
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className={styles.pickerFoot}>
              <button
                className={styles.btnSecondary}
                onClick={() => setPickerAberto(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}